import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  User, Bike, Plus, X, Search, ChevronLeft, ChevronRight, Check, Calendar, Clock,
  Layers, Ruler, Copy, ClipboardCheck, Trash2, Timer, MapPin, Tag, CreditCard,
} from "lucide-react";
import { toast } from "sonner";

const eur = (v) => `€ ${Number(v || 0).toFixed(2)}`;
const isoDay = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const addDaysIso = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return isoDay(d); };
const fmtSlotDate = (iso) => { try { return new Date(`${iso}T00:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" }); } catch { return iso; } };
const _toMin = (hm) => { const [h, m] = String(hm || "0:0").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const deduceFasciaId = (sl) => {
  if (!sl || !sl.length) return null;
  const d = new Date(); const cur = d.getHours() * 60 + d.getMinutes();
  const within = sl.find((s) => _toMin(s.ora_inizio) <= cur && cur < _toMin(s.ora_fine));
  if (within) return within.id;
  return (sl.find((s) => s.tipo_tariffa === "giornata") || sl[0]).id;
};

const STEPS = ["Cliente", "Bici", "Riepilogo", "Conferma"];

export function BookingWizard({ onClose, onCreated, onImmediateCheckout, prefill }) {
  const [step, setStep] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [slots, setSlots] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [prefillDone, setPrefillDone] = useState(false);

  // step 1 - cliente
  const [customer, setCustomer] = useState(null);
  const [cQuery, setCQuery] = useState("");
  const [newMode, setNewMode] = useState(false);
  const [nc, setNc] = useState({ nome: "", cognome: "", email: "", telefono: "", consenso_privacy: false, consenso_marketing: false });

  // step 2 - periodo + carrello bici
  const tomorrow = isoDay(new Date(Date.now() + 86400000));
  const today = isoDay(new Date());
  const [immediato, setImmediato] = useState(false);
  const [startDate, setStartDate] = useState(prefill?.date || tomorrow);
  const [fasce, setFasce] = useState(prefill?.fasciaId ? [prefill.fasciaId] : []);
  const [cat, setCat] = useState(prefill?.categoriaId || "");
  const [taglia, setTaglia] = useState("all");
  const [avail, setAvail] = useState([]);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [cart, setCart] = useState([]);
  const [sedi, setSedi] = useState([]);
  const [sedeRitiro, setSedeRitiro] = useState("");

  // step 3
  const [accQty, setAccQty] = useState({});
  const accPayload = Object.entries(accQty).map(([id, qta]) => ({ id, qta }));
  const setQty = (id, q) => setAccQty((s) => { const n = { ...s }; if (q <= 0) delete n[id]; else n[id] = q; return n; });
  const [note, setNote] = useState("");
  const [quote, setQuote] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [codice, setCodice] = useState("");
  const [metodoPreferito, setMetodoPreferito] = useState("none");
  const [enabledMethods, setEnabledMethods] = useState([]);

  // step 4
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try { setCustomers((await api.get("/customers")).data); } catch (e) {}
      try { setCategories((await api.get("/categories")).data); } catch (e) {}
      try { const s = (await api.get("/timeslots", { params: { solo_attive: true } })).data; setSlots(s); if (s.length && !prefill?.fasciaId) setFasce([(s.find((x) => x.tipo_tariffa === "giornata") || s[0]).id]); } catch (e) {}
      try { setAccessories((await api.get("/pricing/accessories")).data); } catch (e) {}
      try { setSedi((await api.get("/sedi")).data); } catch (e) {}
      try { setCampaigns((await api.get("/pricing/discounts/active")).data); } catch (e) {}
      try { setEnabledMethods((await api.get("/public/config")).data.metodi_pagamento || []); } catch (e) {}
    })();
  }, []);

  useEffect(() => { if (customer && !sedeRitiro) setSedeRitiro(customer.sede_preferita || ""); /* eslint-disable-next-line */ }, [customer]);
  const sedeName = (id) => sedi.find((s) => s.id === id)?.nome || "";

  const slotById = (id) => slots.find((s) => s.id === id);
  const catName = (id) => categories.find((c) => c.id === id)?.nome || "";
  const accName = (id) => accessories.find((a) => a.id === id)?.nome || id;
  const giorniPayload = () => fasce.map((fid, i) => ({ data: addDaysIso(startDate, i), fascia_id: fid }));
  const derived = () => {
    const g = giorniPayload(); if (!g.length) return null;
    const f0 = slotById(g[0].fascia_id), fl = slotById(g[g.length - 1].fascia_id);
    if (!f0 || !fl) return null;
    return { giorni: g, inizio: `${g[0].data}T${f0.ora_inizio}:00`, fine: `${g[g.length - 1].data}T${fl.ora_fine}:00`, fasce_tipi: g.map((x) => slotById(x.fascia_id)?.tipo_tariffa) };
  };

  const filteredCustomers = useMemo(() => {
    const q = cQuery.toLowerCase();
    return customers.filter((c) => [c.nome, c.cognome, c.email, c.telefono].join(" ").toLowerCase().includes(q)).slice(0, 8);
  }, [customers, cQuery]);

  const createCustomer = async () => {
    if (!nc.nome.trim() || !nc.cognome.trim() || !nc.email.trim()) { toast.error("Nome, cognome ed email obbligatori"); return; }
    if (!nc.consenso_privacy) { toast.error("Il consenso privacy è obbligatorio"); return; }
    try {
      const res = await api.post("/customers", nc);
      setCustomers((cs) => [res.data, ...cs]);
      setCustomer(res.data); setNewMode(false);
      toast.success("Cliente creato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione cliente"); }
  };

  const fetchAvail = async (categoria_id) => {
    const d = derived(); if (!categoria_id || !d) { setAvail([]); return; }
    setLoadingAvail(true);
    try {
      const res = await api.post("/rentals/availability", { categoria_id, giorni: d.giorni });
      setAvail(res.data);
    } catch (e) { setAvail([]); toast.error(e.response?.data?.detail || "Errore disponibilità"); }
    finally { setLoadingAvail(false); }
  };
  useEffect(() => { if (cat) fetchAvail(cat); setTaglia("all"); /* eslint-disable-next-line */ }, [cat, startDate, JSON.stringify(fasce), slots.length]);

  const taglie = useMemo(() => Array.from(new Set(avail.map((b) => b.taglia || "—"))), [avail]);
  const shownBikes = avail.filter((b) => (taglia === "all" || (b.taglia || "—") === taglia) && !cart.some((c) => c.bike_id === b.id))
    .sort((a, b) => ((a.sede_attuale === sedeRitiro || !sedeRitiro) ? 0 : 1) - ((b.sede_attuale === sedeRitiro || !sedeRitiro) ? 0 : 1));
  const addToCart = (b) => { setCart((c) => [...c, { bike_id: b.id, numero: b.numero, modello: b.modello, taglia: b.taglia, categoria_id: cat, categoria_nome: catName(cat), sede_attuale: b.sede_attuale, sede_nome: b.sede_nome }]); };
  const removeCart = (id) => setCart((c) => c.filter((x) => x.bike_id !== id));

  // Prefill da calendario: aggiungi automaticamente la bici cliccata al carrello quando è disponibile
  useEffect(() => {
    if (prefillDone || !prefill?.bikeId) return;
    const b = avail.find((x) => x.id === prefill.bikeId);
    if (b) { addToCart(b); setPrefillDone(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avail, prefill, prefillDone]);

  const computeQuote = async () => {
    const d = derived(); if (!d || !cart.length) { setQuote(null); return; }
    try {
      let totale = 0, cauzione = 0, dettaglio = [], sconto = null, sconto_errore = null;
      for (let i = 0; i < cart.length; i++) {
        const b = cart[i];
        const r = await api.post("/pricing/quote", { categoria_id: b.categoria_id, inizio: d.inizio, fine: d.fine, fasce_tipi: d.fasce_tipi, accessori: i === 0 ? accPayload : [], codice_sconto: i === 0 ? codice : "" });
        totale += r.data.totale || 0; cauzione += r.data.cauzione || 0;
        dettaglio.push({ bici: b.numero, importo: r.data.totale });
        if (i === 0) { sconto = r.data.sconto; sconto_errore = r.data.sconto_errore; }
      }
      setQuote({ totale: Math.round(totale * 100) / 100, cauzione: Math.round(cauzione * 100) / 100, dettaglio, sconto, sconto_errore });
    } catch (e) { setQuote(null); }
  };
  useEffect(() => { if (step === 2) computeQuote(); /* eslint-disable-next-line */ }, [step, JSON.stringify(cart), JSON.stringify(accQty), codice]);

  const canNext = () => {
    if (step === 0 && !customer) { toast.error("Seleziona o crea un cliente"); return false; }
    if (step === 1) {
      if (!fasce.length) { toast.error("Seleziona almeno un giorno/fascia"); return false; }
      if (!cart.length) { toast.error("Aggiungi almeno una bici al carrello"); return false; }
    }
    return true;
  };

  const confirm = async () => {
    const d = derived(); if (!d) return;
    setCreating(true);
    try {
      const res = await api.post("/rentals/cart", {
        customer_id: customer.id, giorni: d.giorni, bike_ids: cart.map((c) => c.bike_id),
        accessori: accPayload, note, sede_ritiro: sedeRitiro || "", codice_sconto: codice, origin_url: window.location.origin,
        metodo_preferito: immediato ? "" : (metodoPreferito === "none" ? "" : metodoPreferito),
        checkout_immediato: immediato, invia_email_permalink: !immediato,
      });
      if (immediato && res.data?.rental) {
        onCreated && onCreated();
        onClose();
        onImmediateCheckout && onImmediateCheckout(res.data.rental);
        toast.success("Prenotazione confermata · procedi con il check-out");
        return;
      }
      setResult(res.data); setStep(3);
      onCreated && onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione"); }
    finally { setCreating(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#141414] border-zinc-800 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="booking-wizard">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><Bike className="h-5 w-5 text-[#f98125]" /> Nuova prenotazione</DialogTitle>
          <DialogDescription>Procedura guidata: cliente, bici, riepilogo e conferma con caparra.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1.5 my-2">{STEPS.map((s, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[#f98125]" : "bg-zinc-800"}`} />)}</div>
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Passo {step + 1}/4 · {STEPS[step]}</div>

        <div className="min-h-[260px]">
          {/* STEP 1 CLIENTE */}
          {step === 0 && (
            <div className="space-y-4" data-testid="wiz-step-cliente">
              {customer && (
                <div className="flex items-center justify-between bg-[#f98125]/10 border border-[#f98125]/40 rounded-sm p-3" data-testid="wiz-selected-customer">
                  <div className="flex items-center gap-2"><User className="h-4 w-4 text-[#f98125]" /><div><div className="font-medium">{customer.nome} {customer.cognome}</div><div className="text-xs text-zinc-500">{customer.email}</div></div></div>
                  <button onClick={() => setCustomer(null)} className="text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
                </div>
              )}
              {!customer && !newMode && (
                <>
                  <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                    <Input value={cQuery} onChange={(e) => setCQuery(e.target.value)} placeholder="Cerca cliente per nome, email, telefono..." className="pl-9 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="wiz-customer-search" /></div>
                  <div className="space-y-1 max-h-52 overflow-y-auto">
                    {filteredCustomers.map((c) => (
                      <button key={c.id} onClick={() => setCustomer(c)} data-testid={`wiz-customer-${c.id}`} className="w-full text-left px-3 py-2 rounded-sm border border-zinc-800 hover:border-[#f98125] transition-colors">
                        <div className="text-sm font-medium">{c.nome} {c.cognome}</div><div className="text-xs text-zinc-500">{c.email} · {c.telefono || "—"}</div>
                      </button>
                    ))}
                    {filteredCustomers.length === 0 && <div className="text-sm text-zinc-600 py-4 text-center">Nessun cliente trovato</div>}
                  </div>
                  <button onClick={() => setNewMode(true)} data-testid="wiz-new-customer-btn" className="flex items-center gap-2 text-sm text-[#f98125] hover:text-[#d96a12]"><Plus className="h-4 w-4" /> Nuovo cliente al volo</button>
                </>
              )}
              {!customer && newMode && (
                <div className="space-y-3" data-testid="wiz-new-customer-form">
                  <div className="grid grid-cols-2 gap-3">
                    <Input value={nc.nome} onChange={(e) => setNc({ ...nc, nome: e.target.value })} placeholder="Nome *" className="bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="wiz-nc-nome" />
                    <Input value={nc.cognome} onChange={(e) => setNc({ ...nc, cognome: e.target.value })} placeholder="Cognome *" className="bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="wiz-nc-cognome" />
                  </div>
                  <Input value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} placeholder="Email *" className="bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="wiz-nc-email" />
                  <Input value={nc.telefono} onChange={(e) => setNc({ ...nc, telefono: e.target.value })} placeholder="Telefono" className="bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="wiz-nc-tel" />
                  <label className="flex items-center gap-2 text-sm text-zinc-300"><Checkbox checked={nc.consenso_privacy} onCheckedChange={(v) => setNc({ ...nc, consenso_privacy: !!v })} data-testid="wiz-nc-privacy" /> Consenso privacy (obbligatorio)</label>
                  <label className="flex items-center gap-2 text-sm text-zinc-300"><Checkbox checked={nc.consenso_marketing} onCheckedChange={(v) => setNc({ ...nc, consenso_marketing: !!v })} data-testid="wiz-nc-marketing" /> Consenso marketing</label>
                  <div className="flex gap-2">
                    <button onClick={() => setNewMode(false)} className="rounded-sm border border-zinc-700 px-4 py-2 text-sm hover:bg-zinc-800">Annulla</button>
                    <button onClick={createCustomer} data-testid="wiz-nc-save" className="rounded-sm bg-[#f98125] text-white font-bold px-4 py-2 text-sm hover:bg-[#d96a12]">Crea e seleziona</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 2 BICI */}
          {step === 1 && (
            <div className="space-y-4" data-testid="wiz-step-bici">
              <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-3 space-y-3">
                <div className="flex items-center justify-between gap-2 pb-2 border-b border-zinc-800/70">
                  <div className="flex items-center gap-2">
                    <Timer className="h-4 w-4 text-[#39FF14]" />
                    <div>
                      <div className="text-sm font-medium leading-none">Check-out immediato</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">Noleggio al banco: data = oggi, si va subito alla consegna (no caparra, no blocco 6h).</div>
                    </div>
                  </div>
                  <Switch checked={immediato} data-testid="wiz-immediate-toggle"
                    onCheckedChange={(v) => {
                      setImmediato(v);
                      if (v) { setStartDate(today); const fid = deduceFasciaId(slots); setFasce(fid ? [fid] : []); }
                    }} />
                </div>
                <div className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Periodo del noleggio</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input type="date" value={immediato ? today : startDate} min={isoDay(new Date())} disabled={immediato} onChange={(e) => setStartDate(e.target.value)} className="bg-[#141414] border-zinc-800 rounded-sm w-44 disabled:opacity-60" data-testid="wiz-start-date" />
                  {immediato && <span className="text-[11px] text-[#39FF14] flex items-center gap-1" data-testid="wiz-immediate-badge"><Clock className="h-3 w-3" /> Oggi · consegna adesso · fascia dedotta (modificabile)</span>}
                  {!immediato && <button type="button" onClick={() => setFasce((f) => [...f, (slots.find((x) => x.tipo_tariffa === "giornata") || slots[0])?.id])} className="flex items-center gap-1 text-xs text-[#f98125]"><Plus className="h-3.5 w-3.5" /> Giorno</button>}
                </div>
                {fasce.map((fid, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`wiz-day-${i}`}>
                    <span className="w-28 text-xs text-zinc-400 flex items-center gap-1"><Clock className="h-3 w-3 text-zinc-600" /> {fmtSlotDate(addDaysIso(startDate, i))}</span>
                    <Select value={fid} onValueChange={(v) => setFasce((f) => f.map((x, idx) => idx === i ? v : x))}>
                      <SelectTrigger className="bg-[#141414] border-zinc-800 rounded-sm h-9 flex-1" data-testid={`wiz-fascia-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#141414] border-zinc-800">{slots.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome} ({s.ora_inizio}–{s.ora_fine})</SelectItem>)}</SelectContent>
                    </Select>
                    {fasce.length > 1 && <button onClick={() => setFasce((f) => f.filter((_, idx) => idx !== i))} className="p-1.5 text-zinc-600 hover:text-[#FF3B30]"><X className="h-4 w-4" /></button>}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Layers className="h-3 w-3" /> Tipologia</Label>
                  <Select value={cat} onValueChange={setCat}>
                    <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="wiz-categoria"><SelectValue placeholder="Categoria" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-zinc-800">{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Ruler className="h-3 w-3" /> Taglia</Label>
                  <Select value={taglia} onValueChange={setTaglia} disabled={!cat}>
                    <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="wiz-taglia"><SelectValue placeholder="Tutte" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-zinc-800"><SelectItem value="all">Tutte le taglie</SelectItem>{taglie.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>

              {sedi.length > 0 && (
                <div><Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> Sede di ritiro</Label>
                  <Select value={sedeRitiro || "none"} onValueChange={(v) => setSedeRitiro(v === "none" ? "" : v)}>
                    <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="wiz-sede-ritiro"><SelectValue placeholder="Sede di ritiro" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-zinc-800"><SelectItem value="none">Nessuna sede</SelectItem>{sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
                  </Select></div>
              )}

              {cat && (
                <div data-testid="wiz-avail-list">
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Disponibili per il periodo {loadingAvail && "…"}</div>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                    {shownBikes.map((b) => {
                      const transfer = sedeRitiro && b.sede_attuale !== sedeRitiro;
                      return (
                        <button key={b.id} onClick={() => addToCart(b)} data-testid={`wiz-bike-${b.id}`} className={`flex flex-col items-start gap-0.5 px-3 py-2 rounded-sm border text-sm ${transfer ? "border-amber-500/50 bg-amber-500/5 hover:border-amber-500" : "border-zinc-800 hover:border-[#f98125]"}`}>
                          <span className="flex items-center justify-between w-full"><span>Bici {b.numero} {b.taglia ? `· ${b.taglia}` : ""}</span><Plus className="h-4 w-4 text-[#f98125]" /></span>
                          {transfer && <span className="text-[10px] text-amber-400">da trasferire da {b.sede_nome || "nessuna sede"} a {sedeName(sedeRitiro)}</span>}
                        </button>
                      );
                    })}
                    {!loadingAvail && shownBikes.length === 0 && <div className="col-span-2 text-sm text-zinc-600 py-4 text-center">Nessuna bici disponibile</div>}
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center justify-between"><span>Carrello bici</span><span className="text-[#f98125]">{cart.length}</span></div>
                {cart.length === 0 ? <div className="text-sm text-zinc-600">Vuoto — aggiungi almeno una bici.</div> : (
                  <div className="space-y-1.5" data-testid="wiz-cart">
                    {cart.map((c) => (
                      <div key={c.bike_id} className="flex items-center justify-between bg-[#0A0A0A] border border-zinc-800 rounded-sm px-3 py-2" data-testid={`wiz-cart-${c.bike_id}`}>
                        <span className="text-sm">Bici {c.numero} · {c.categoria_nome}{c.taglia ? ` · ${c.taglia}` : ""}</span>
                        <button onClick={() => removeCart(c.bike_id)} className="text-zinc-600 hover:text-[#FF3B30]"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 3 RIEPILOGO */}
          {step === 2 && (
            <div className="space-y-4" data-testid="wiz-step-riepilogo">
              <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-zinc-500">Cliente</span><span>{customer?.nome} {customer?.cognome}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Bici</span><span>{cart.map((c) => c.numero).join(", ")}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Periodo</span><span>{fmtSlotDate(startDate)} · {fasce.length} g</span></div>
              </div>
              {accessories.length > 0 && (
                <div><Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Accessori</Label>
                  <div className="grid grid-cols-1 gap-2">{accessories.map((a) => {
                    const q = accQty[a.id] || 0;
                    return (
                    <div key={a.id} className={`flex items-center justify-between gap-2 px-3 py-2 rounded-sm border text-sm ${q > 0 ? "border-[#f98125] bg-[#f98125]/10" : "border-zinc-800"}`} data-testid={`wiz-acc-${a.id}`}>
                      <span className="flex-1">{a.nome} <span className="text-zinc-500">({eur(a.prezzo)})</span></span>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => setQty(a.id, q - 1)} className="h-7 w-7 rounded-sm border border-zinc-700 hover:bg-zinc-800 disabled:opacity-40" disabled={q <= 0} data-testid={`wiz-acc-minus-${a.id}`}>−</button>
                        <span className="w-6 text-center font-mono" data-testid={`wiz-acc-qty-${a.id}`}>{q}</span>
                        <button type="button" onClick={() => setQty(a.id, q + 1)} className="h-7 w-7 rounded-sm border border-zinc-700 hover:bg-zinc-800" data-testid={`wiz-acc-plus-${a.id}`}>+</button>
                      </div>
                    </div>);
                  })}</div></div>
              )}
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Note</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" rows={2} data-testid="wiz-note" /></div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5"><Tag className="h-3.5 w-3.5 text-[#f98125]" /> Campagna sconto</Label>
                <Select value={codice || "__none__"} onValueChange={(v) => setCodice(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="wiz-codice-select"><SelectValue placeholder="Nessuno sconto" /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-zinc-800">
                    <SelectItem value="__none__">Nessuno sconto</SelectItem>
                    {campaigns.map((c) => (
                      <SelectItem key={c.id} value={c.codice} data-testid={`wiz-camp-${c.id}`}>
                        {c.codice} · {c.tipo === "fisso" ? eur(c.importo_fisso) : `${c.percentuale}%`}{c.descrizione ? ` — ${c.descrizione}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {campaigns.length === 0 && <p className="text-[11px] text-zinc-600 mt-1">Nessuna campagna attiva al momento.</p>}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5 text-[#f98125]" /> Metodo di pagamento preferito <span className="text-zinc-600 normal-case">(prenotazione telefonica)</span></Label>
                {immediato ? (
                  <p className="text-[11px] text-zinc-500 mt-1.5" data-testid="wiz-immediate-pay-note">Check-out immediato: nessuna caparra. Il pagamento verrà registrato al check-out o al check-in con il metodo scelto (incluso Contanti).</p>
                ) : (
                <>
                <Select value={metodoPreferito} onValueChange={setMetodoPreferito}>
                  <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="wiz-metodo-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-zinc-800">
                    <SelectItem value="none">Lascia scegliere al cliente</SelectItem>
                    {enabledMethods.map((m) => (
                      <SelectItem key={m.key} value={m.key} data-testid={`wiz-metodo-${m.key}`}>{m.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-zinc-600 mt-1">Il cliente riceverà via email il link di pagamento (permalink). Se indichi un metodo, verrà evidenziato nella pagina.</p>
                </>
                )}
              </div>
              {quote && (
                <div className="border border-[#f98125]/30 bg-[#f98125]/5 rounded-sm p-3 space-y-1" data-testid="wiz-quote">
                  {quote.dettaglio.map((d, i) => <div key={i} className="flex justify-between text-xs text-zinc-400"><span>Bici {d.bici}</span><span>{eur(d.importo)}</span></div>)}
                  {quote.sconto && <div className="flex justify-between text-xs text-[#39FF14]" data-testid="wiz-sconto"><span>Sconto {quote.sconto.codice ? `(${quote.sconto.codice})` : ""}</span><span>− {eur(quote.sconto.importo)}</span></div>}
                  {quote.sconto_errore && <div className="text-[#FF9500] text-[11px]" data-testid="wiz-sconto-err">⚠ {quote.sconto_errore}</div>}
                  <div className="flex justify-between items-center pt-1.5 border-t border-zinc-800/60"><span className="text-sm">Totale</span><span className="font-display text-2xl text-[#f98125]" data-testid="wiz-totale">{eur(quote.totale)}</span></div>
                  <div className="flex justify-between text-[11px] text-zinc-500"><span>Cauzione</span><span>{eur(quote.cauzione)}</span></div>
                </div>
              )}
            </div>
          )}

          {/* STEP 4 CONFERMA */}
          {step === 3 && result && (
            <div className="space-y-4 text-center py-4" data-testid="wiz-step-conferma">
              <div className="h-14 w-14 rounded-full bg-[#39FF14]/15 mx-auto flex items-center justify-center"><Check className="h-7 w-7 text-[#39FF14]" /></div>
              <div className="font-display text-2xl">Prenotazione creata</div>
              <div className="text-sm text-zinc-400 flex items-center justify-center gap-2"><Timer className="h-4 w-4 text-[#f98125]" /> Bici bloccate per 6 ore in attesa del pagamento caparra.</div>
              <div className="text-sm text-zinc-400">Email con il link per il pagamento inviata a <b>{customer?.email}</b>.</div>
              <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-3 flex items-center gap-2">
                <input readOnly value={result.permalink} className="flex-1 bg-transparent text-xs text-zinc-300 outline-none" data-testid="wiz-permalink" />
                <button onClick={() => { navigator.clipboard?.writeText(result.permalink); toast.success("Link copiato"); }} data-testid="wiz-copy-link" className="flex items-center gap-1 text-sm text-[#f98125]"><Copy className="h-4 w-4" /> Copia</button>
              </div>
              <a href={result.permalink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white"><ClipboardCheck className="h-4 w-4" /> Apri il riepilogo pagamento</a>
            </div>
          )}
        </div>

        {/* nav */}
        {step < 3 && (
          <div className="flex items-center justify-between gap-2 pt-3 border-t border-zinc-800">
            <button onClick={() => step === 0 ? onClose() : setStep(step - 1)} data-testid="wiz-back" className="flex items-center gap-1 rounded-sm border border-zinc-700 px-4 py-2.5 text-sm hover:bg-zinc-800"><ChevronLeft className="h-4 w-4" /> {step === 0 ? "Annulla" : "Indietro"}</button>
            {step < 2
              ? <button onClick={() => canNext() && setStep(step + 1)} data-testid="wiz-next" className="flex items-center gap-1 rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 text-sm hover:bg-[#d96a12]">Avanti <ChevronRight className="h-4 w-4" /></button>
              : <button onClick={() => !creating && confirm()} data-testid="wiz-confirm" className="flex items-center gap-1 rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 text-sm hover:bg-[#d96a12]"><Check className="h-4 w-4" /> {creating ? "Creazione…" : (immediato ? "Conferma e vai al check-out" : "Conferma e blocca 6h")}</button>}
          </div>
        )}
        {step === 3 && (
          <div className="pt-3 border-t border-zinc-800 text-right"><button onClick={onClose} data-testid="wiz-close" className="rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 text-sm hover:bg-[#d96a12]">Chiudi</button></div>
        )}
      </DialogContent>
    </Dialog>
  );
}
